import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Search,
  Filter,
  Eye,
  Edit2,
  History,
  UserPlus,
  X,
  TrendingUp,
  Award,
  Building2,
  Users,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
  BookOpen,
  Calendar,
  Layers,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

export const StudentDataPage: React.FC = () => {
  const { user, fetchWithAuth } = useAuth();

  // Data & Pagination state
  const [students, setStudents] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(15);

  // Filters
  const [searchInput, setSearchInput] = useState<string>('');
  const [activeSearch, setActiveSearch] = useState<string>('');
  const [selectedBlock, setSelectedBlock] = useState<string>('all');
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [selectedStream, setSelectedStream] = useState<string>('all');
  const [selectedSection, setSelectedSection] = useState<string>('all');
  const [selectedGender, setSelectedGender] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  // Notification Banner
  const [bannerMessage, setBannerMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Modal States
  const [addModalOpen, setAddModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [historyModalOpen, setHistoryModalOpen] = useState<boolean>(false);
  const [profileModalOpen, setProfileModalOpen] = useState<boolean>(false);

  // Modal Payloads
  const [selectedStudent, setSelectedStudent] = useState<any | null>(null);
  const [historyData, setHistoryData] = useState<{ student: any; history: any[] } | null>(null);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);

  // Add Form State
  const [addForm, setAddForm] = useState({
    srn: '',
    student_name: '',
    father_name: '',
    mother_name: '',
    gender: 'Male',
    class_id: '1',
    stream: 'Arts',
    section: 'A',
    academic_session_id: '3'
  });
  const [addError, setAddError] = useState<string | null>(null);
  const [addSaving, setAddSaving] = useState<boolean>(false);

  // Edit Form State
  const [editForm, setEditForm] = useState({
    id: 0,
    srn: '',
    student_name: '',
    father_name: '',
    mother_name: '',
    gender: 'Male',
    class_id: '1',
    stream: 'Arts',
    section: 'A',
    status: 'ACTIVE'
  });
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState<boolean>(false);

  const loadStudents = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (activeSearch && activeSearch.trim()) query.set('search', activeSearch.trim());
      if (user?.role !== 'school' && selectedBlock !== 'all') query.set('block', selectedBlock);
      if (selectedClass !== 'all') query.set('class_id', selectedClass);
      if (selectedStream !== 'all') query.set('stream', selectedStream);
      if (selectedSection !== 'all') query.set('section', selectedSection);
      if (selectedGender !== 'all') query.set('gender', selectedGender);
      if (selectedStatus !== 'all') query.set('status', selectedStatus);
      query.set('page', String(page));
      query.set('limit', String(limit));

      const res = await fetchWithAuth(`/api/students-master?${query.toString()}`);
      const json = await res.json();
      if (json.success) {
        setStudents(json.data.students || []);
        setTotalCount(json.data.pagination?.total || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
  }, [page, activeSearch, selectedBlock, selectedClass, selectedStream, selectedSection, selectedGender, selectedStatus]);

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setActiveSearch(searchInput);
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setActiveSearch('');
    setSelectedBlock('all');
    setSelectedClass('all');
    setSelectedStream('all');
    setSelectedSection('all');
    setSelectedGender('all');
    setSelectedStatus('all');
    setPage(1);
  };

  // Open 360° Profile
  const openProfile = async (studentId: number) => {
    try {
      const res = await fetchWithAuth(`/api/students/${studentId}/profile`);
      const json = await res.json();
      if (json.success) {
        setSelectedStudent(json.data);
        setProfileModalOpen(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Open Assessment History Modal
  const openHistory = async (studentId: number) => {
    setHistoryLoading(true);
    setHistoryModalOpen(true);
    try {
      const res = await fetchWithAuth(`/api/students/${studentId}/assessment-history`);
      const json = await res.json();
      if (json.success) {
        setHistoryData(json.data);
      } else {
        setHistoryData(null);
      }
    } catch (e) {
      console.error(e);
      setHistoryData(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Open Edit Modal
  const openEdit = (st: any) => {
    setEditForm({
      id: st.id,
      srn: st.srn,
      student_name: st.student_name,
      father_name: st.father_name || '',
      mother_name: st.mother_name || '',
      gender: st.gender || 'Male',
      class_id: String(st.class_id || (st.class_name?.includes('12') ? '2' : '1')),
      stream: st.stream || 'Arts',
      section: st.section || 'A',
      status: (st.enrollment_status || st.status || 'ACTIVE').toUpperCase().replace('_', ' ')
    });
    setEditError(null);
    setEditModalOpen(true);
  };

  // Submit Add Form
  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddSaving(true);
    setAddError(null);

    if (!addForm.srn.trim()) {
      setAddError('SRN is required.');
      setAddSaving(false);
      return;
    }
    if (!addForm.student_name.trim()) {
      setAddError('Student Name is required.');
      setAddSaving(false);
      return;
    }

    try {
      const payload = {
        srn: addForm.srn.trim(),
        student_name: addForm.student_name.trim(),
        father_name: addForm.father_name.trim() || null,
        mother_name: addForm.mother_name.trim() || null,
        gender: addForm.gender,
        class_id: parseInt(addForm.class_id, 10),
        stream: addForm.class_id === '2' ? addForm.stream : null,
        section: addForm.section.trim().toUpperCase() || 'A',
        academic_session_id: parseInt(addForm.academic_session_id, 10) || 3
      };

      const res = await fetchWithAuth('/api/students', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (json.success) {
        setAddModalOpen(false);
        setAddForm({
          srn: '',
          student_name: '',
          father_name: '',
          mother_name: '',
          gender: 'Male',
          class_id: '1',
          stream: 'Arts',
          section: 'A',
          academic_session_id: '3'
        });
        setBannerMessage({ type: 'success', text: `Student "${json.data?.student_name || 'Record'}" added successfully to school registry.` });
        loadStudents();
      } else {
        setAddError(json.message || 'Failed to add student.');
      }
    } catch (e: any) {
      setAddError('Network error adding student.');
    } finally {
      setAddSaving(false);
    }
  };

  // Submit Edit Form
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditSaving(true);
    setEditError(null);

    if (!editForm.student_name.trim()) {
      setEditError('Student Name is required.');
      setEditSaving(false);
      return;
    }

    try {
      const payload = {
        student_name: editForm.student_name.trim(),
        father_name: editForm.father_name.trim() || null,
        mother_name: editForm.mother_name.trim() || null,
        gender: editForm.gender,
        class_id: parseInt(editForm.class_id, 10),
        stream: editForm.class_id === '2' ? editForm.stream : null,
        section: editForm.section.trim().toUpperCase() || 'A',
        status: editForm.status
      };

      const res = await fetchWithAuth(`/api/students/${editForm.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (json.success) {
        setEditModalOpen(false);
        setBannerMessage({ type: 'success', text: `Student "${json.data?.student_name || 'Record'}" updated successfully.` });
        loadStudents();
      } else {
        setEditError(json.message || 'Failed to update student.');
      }
    } catch (e: any) {
      setEditError('Network error updating student.');
    } finally {
      setEditSaving(false);
    }
  };

  const getStatusBadge = (statusStr: string) => {
    const s = (statusStr || 'ACTIVE').toUpperCase().replace('_', ' ');
    if (s === 'ACTIVE') {
      return <span className="bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded text-[11px] border border-emerald-300">ACTIVE</span>;
    } else if (s === 'TRANSFERRED') {
      return <span className="bg-blue-100 text-blue-800 font-bold px-2 py-0.5 rounded text-[11px] border border-blue-300">TRANSFERRED</span>;
    } else if (s === 'LEFT SCHOOL' || s === 'LEFT_SCHOOL') {
      return <span className="bg-rose-100 text-rose-800 font-bold px-2 py-0.5 rounded text-[11px] border border-rose-300">LEFT SCHOOL</span>;
    } else if (s === 'DROPPED OUT' || s === 'DROPPED_OUT') {
      return <span className="bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded text-[11px] border border-amber-300">DROPPED OUT</span>;
    } else if (s === 'PASSED' || s === 'PASSED/COMPLETED' || s === 'PASSED_COMPLETED') {
      return <span className="bg-purple-100 text-purple-800 font-bold px-2 py-0.5 rounded text-[11px] border border-purple-300">PASSED/COMPLETED</span>;
    } else if (s === 'INACTIVE') {
      return <span className="bg-slate-200 text-slate-700 font-bold px-2 py-0.5 rounded text-[11px] border border-slate-300">INACTIVE</span>;
    }
    return <span className="bg-slate-100 text-slate-600 font-bold px-2 py-0.5 rounded text-[11px] border border-slate-300">{s}</span>;
  };

  const totalPages = Math.ceil(totalCount / limit) || 1;

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
            Official Student Master Registry & Enrollment
          </div>
          <h1 className="text-lg md:text-xl font-bold text-[#0F2C59]">
            {user?.role === 'school' ? 'My School Students' : 'Student Master Registry & Academic Profiles'}
          </h1>
          <div className="text-xs text-slate-500 mt-0.5">
            Total Enrolled in Registry: <strong className="text-slate-900">{totalCount.toLocaleString('en-IN')}</strong> {user?.role === 'school' ? `(${user.name || 'Your School'})` : '(Nuh District Govt Schools)'}
          </div>
        </div>

        <div className="flex items-center space-x-3 w-full md:w-auto justify-end">
          <button
            onClick={() => {
              setAddError(null);
              setAddModalOpen(true);
            }}
            className="inline-flex items-center space-x-1.5 bg-[#0F2C59] hover:bg-[#1E40AF] text-white px-3.5 py-2 rounded text-xs font-bold shadow-sm transition"
          >
            <UserPlus className="w-4 h-4 text-amber-400" />
            <span>+ Add Student</span>
          </button>
        </div>
      </div>

      {/* Banner Alert */}
      {bannerMessage && (
        <div className={`p-3 rounded-lg border text-xs flex items-center justify-between ${
          bannerMessage.type === 'success'
            ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
            : 'bg-rose-50 border-rose-300 text-rose-900'
        }`}>
          <div className="flex items-center space-x-2">
            {bannerMessage.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span className="font-semibold">{bannerMessage.text}</span>
          </div>
          <button onClick={() => setBannerMessage(null)} className="text-slate-500 hover:text-slate-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* FILTER & SEARCH RIBBON */}
      <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200 space-y-3">
        <form onSubmit={handleSearchSubmit} className="flex flex-wrap items-center gap-2 text-xs">
          {/* Search Box */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by SRN, Student Name, Father's Name..."
              className="w-full pl-9 pr-3 py-1.5 text-xs border border-slate-300 rounded focus:ring-1 focus:ring-[#0F2C59] bg-white text-slate-900"
            />
          </div>

          {/* Block (Only if DEO/Admin) */}
          {user?.role !== 'school' && (
            <div className="flex items-center space-x-1">
              <span className="font-semibold text-slate-700">Block:</span>
              <select
                value={selectedBlock}
                onChange={(e) => { setSelectedBlock(e.target.value); setPage(1); }}
                className="bg-white border border-slate-300 py-1.5 px-2 rounded font-medium text-slate-800 text-xs"
              >
                <option value="all">All Blocks</option>
                <option value="Nuh">Nuh</option>
                <option value="Nagina">Nagina</option>
                <option value="Taoru">Taoru</option>
                <option value="Ferozepur Jhirka">Ferozepur Jhirka</option>
                <option value="Punhana">Punhana</option>
              </select>
            </div>
          )}

          {/* Class */}
          <div className="flex items-center space-x-1">
            <span className="font-semibold text-slate-700">Class:</span>
            <select
              value={selectedClass}
              onChange={(e) => { setSelectedClass(e.target.value); setPage(1); }}
              className="bg-white border border-slate-300 py-1.5 px-2 rounded font-medium text-slate-800 text-xs"
            >
              <option value="all">All Classes</option>
              <option value="1">Class 10</option>
              <option value="2">Class 12</option>
            </select>
          </div>

          {/* Stream */}
          <div className="flex items-center space-x-1">
            <span className="font-semibold text-slate-700">Stream:</span>
            <select
              value={selectedStream}
              onChange={(e) => { setSelectedStream(e.target.value); setPage(1); }}
              className="bg-white border border-slate-300 py-1.5 px-2 rounded font-medium text-slate-800 text-xs"
            >
              <option value="all">All Streams</option>
              <option value="Arts">Arts</option>
              <option value="Science">Science</option>
              <option value="Commerce">Commerce</option>
            </select>
          </div>

          {/* Section */}
          <div className="flex items-center space-x-1">
            <span className="font-semibold text-slate-700">Section:</span>
            <select
              value={selectedSection}
              onChange={(e) => { setSelectedSection(e.target.value); setPage(1); }}
              className="bg-white border border-slate-300 py-1.5 px-2 rounded font-medium text-slate-800 text-xs"
            >
              <option value="all">All</option>
              <option value="A">Section A</option>
              <option value="B">Section B</option>
              <option value="C">Section C</option>
              <option value="D">Section D</option>
            </select>
          </div>

          {/* Gender */}
          <div className="flex items-center space-x-1">
            <span className="font-semibold text-slate-700">Gender:</span>
            <select
              value={selectedGender}
              onChange={(e) => { setSelectedGender(e.target.value); setPage(1); }}
              className="bg-white border border-slate-300 py-1.5 px-2 rounded font-medium text-slate-800 text-xs"
            >
              <option value="all">All</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>

          {/* Status */}
          <div className="flex items-center space-x-1">
            <span className="font-semibold text-slate-700">Status:</span>
            <select
              value={selectedStatus}
              onChange={(e) => { setSelectedStatus(e.target.value); setPage(1); }}
              className="bg-white border border-slate-300 py-1.5 px-2 rounded font-medium text-slate-800 text-xs"
            >
              <option value="all">All Statuses</option>
              <option value="ACTIVE">ACTIVE</option>
              <option value="TRANSFERRED">TRANSFERRED</option>
              <option value="LEFT_SCHOOL">LEFT SCHOOL</option>
              <option value="DROPPED_OUT">DROPPED OUT</option>
              <option value="PASSED">PASSED/COMPLETED</option>
              <option value="INACTIVE">INACTIVE</option>
              <option value="OTHER">OTHER</option>
            </select>
          </div>

          {/* Search Button */}
          <button
            type="submit"
            className="inline-flex items-center space-x-1 bg-[#0F2C59] text-white px-3 py-1.5 rounded font-semibold text-xs hover:bg-[#1E40AF] transition"
          >
            <Search className="w-3.5 h-3.5" />
            <span>Search</span>
          </button>

          {/* Reset Button */}
          <button
            type="button"
            onClick={handleResetFilters}
            className="inline-flex items-center space-x-1 bg-white border border-slate-300 text-slate-700 px-3 py-1.5 rounded font-semibold text-xs hover:bg-slate-100 transition"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Reset</span>
          </button>
        </form>
      </div>

      {/* STUDENT REGISTRY TABLE */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-[#0F2C59] text-white uppercase font-bold text-[11px] border-b border-slate-200">
              <tr>
                <th className="py-2.5 px-3 text-center w-12">#</th>
                <th className="py-2.5 px-3 w-28">SRN</th>
                <th className="py-2.5 px-3.5">Student Name</th>
                <th className="py-2.5 px-3">Father's Name</th>
                <th className="py-2.5 px-3">Mother's Name</th>
                <th className="py-2.5 px-2.5 text-center w-16">Gender</th>
                <th className="py-2.5 px-2.5 text-center w-20">Class</th>
                <th className="py-2.5 px-2.5 text-center w-20">Stream</th>
                <th className="py-2.5 px-2 text-center w-14">Section</th>
                <th className="py-2.5 px-3 text-center w-28">Status</th>
                <th className="py-2.5 px-2.5 text-center w-24">Academic Year</th>
                <th className="py-2.5 px-3 text-center w-48">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 font-medium">
              {loading ? (
                <tr>
                  <td colSpan={12} className="py-10 text-center text-slate-500">
                    <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-[#0F2C59] border-t-transparent mb-2"></div>
                    <div>Loading student registry...</div>
                  </td>
                </tr>
              ) : students.length === 0 ? (
                <tr>
                  <td colSpan={12} className="py-10 text-center text-slate-500">
                    No student records found matching the selected filters.
                  </td>
                </tr>
              ) : (
                students.map((st, idx) => {
                  const isClass12 = st.class_name?.includes('12') || st.class_id === 2;
                  const streamText = isClass12 ? (st.stream || 'Arts') : 'N/A';

                  return (
                    <tr key={st.id} className="hover:bg-slate-50 transition">
                      <td className="py-2.5 px-3 text-center font-bold text-slate-700">
                        {(page - 1) * limit + idx + 1}
                      </td>
                      <td className="py-2.5 px-3 text-slate-700 font-mono text-[11px] font-bold">
                        {st.srn}
                      </td>
                      <td className="py-2.5 px-3.5 font-bold text-slate-900">
                        {st.student_name}
                      </td>
                      <td className="py-2.5 px-3 text-slate-600">
                        {st.father_name || '-'}
                      </td>
                      <td className="py-2.5 px-3 text-slate-600">
                        {st.mother_name || '-'}
                      </td>
                      <td className="py-2.5 px-2.5 text-center text-slate-700">
                        {st.gender}
                      </td>
                      <td className="py-2.5 px-2.5 text-center font-semibold text-slate-900">
                        {st.class_name || (st.class_id === 1 ? 'Class 10' : 'Class 12')}
                      </td>
                      <td className="py-2.5 px-2.5 text-center font-medium text-slate-700">
                        {streamText}
                      </td>
                      <td className="py-2.5 px-2 text-center font-bold text-slate-800">
                        {st.section || 'A'}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {getStatusBadge(st.enrollment_status || st.status)}
                      </td>
                      <td className="py-2.5 px-2.5 text-center text-slate-600 font-mono text-[11px]">
                        {st.academic_year || '2026-27'}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <div className="inline-flex items-center space-x-1">
                          <button
                            onClick={() => openProfile(st.id)}
                            title="360° Academic Profile"
                            className="inline-flex items-center space-x-1 bg-slate-100 hover:bg-[#0F2C59] hover:text-white text-[#0F2C59] px-2 py-1 rounded text-[11px] font-semibold transition"
                          >
                            <Eye className="w-3 h-3" />
                            <span>View</span>
                          </button>

                          <button
                            onClick={() => openEdit(st)}
                            title="Edit Student Information"
                            className="inline-flex items-center space-x-1 bg-amber-50 hover:bg-amber-600 hover:text-white text-amber-800 border border-amber-200 px-2 py-1 rounded text-[11px] font-semibold transition"
                          >
                            <Edit2 className="w-3 h-3" />
                            <span>Edit</span>
                          </button>

                          <button
                            onClick={() => openHistory(st.id)}
                            title="Monthly Assessment Marks History"
                            className="inline-flex items-center space-x-1 bg-blue-50 hover:bg-blue-600 hover:text-white text-blue-800 border border-blue-200 px-2 py-1 rounded text-[11px] font-semibold transition"
                          >
                            <History className="w-3 h-3" />
                            <span>History</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        <div className="p-3 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-2 text-xs">
          <div className="text-slate-600">
            Showing <strong className="text-slate-900">{students.length > 0 ? (page - 1) * limit + 1 : 0}</strong> to <strong className="text-slate-900">{Math.min(page * limit, totalCount)}</strong> of <strong className="text-slate-900">{totalCount.toLocaleString('en-IN')}</strong> students
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-slate-500">Page {page} of {totalPages}</span>
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="inline-flex items-center space-x-1 px-2.5 py-1 bg-white border border-slate-300 rounded font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40 transition"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              <span>Previous</span>
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="inline-flex items-center space-x-1 px-2.5 py-1 bg-white border border-slate-300 rounded font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40 transition"
            >
              <span>Next</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* 1. ADD STUDENT MODAL */}
      {addModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full overflow-hidden border border-slate-300 animate-in fade-in zoom-in duration-150">
            <div className="p-4 bg-[#0F2C59] text-white flex justify-between items-center">
              <div>
                <div className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">
                  School Student Registration
                </div>
                <h3 className="text-base font-bold">+ Add New Student to School</h3>
              </div>
              <button
                onClick={() => setAddModalOpen(false)}
                className="p-1 rounded hover:bg-white/20 text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddSubmit} className="p-5 space-y-4 text-xs">
              {addError && (
                <div className="p-3 bg-rose-50 border border-rose-300 rounded text-rose-900 font-bold flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  <span>{addError}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {/* SRN */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">
                    Student SRN <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={addForm.srn}
                    onChange={(e) => setAddForm({ ...addForm, srn: e.target.value })}
                    placeholder="e.g. 2100891234"
                    className="w-full border border-slate-300 rounded p-2 text-xs font-mono font-bold focus:ring-1 focus:ring-[#0F2C59]"
                  />
                </div>

                {/* Student Name */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">
                    Student Name <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={addForm.student_name}
                    onChange={(e) => setAddForm({ ...addForm, student_name: e.target.value })}
                    placeholder="Full Student Name"
                    className="w-full border border-slate-300 rounded p-2 text-xs font-bold focus:ring-1 focus:ring-[#0F2C59]"
                  />
                </div>

                {/* Father Name */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Father's Name</label>
                  <input
                    type="text"
                    value={addForm.father_name}
                    onChange={(e) => setAddForm({ ...addForm, father_name: e.target.value })}
                    placeholder="Father's Name"
                    className="w-full border border-slate-300 rounded p-2 text-xs focus:ring-1 focus:ring-[#0F2C59]"
                  />
                </div>

                {/* Mother Name */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Mother's Name</label>
                  <input
                    type="text"
                    value={addForm.mother_name}
                    onChange={(e) => setAddForm({ ...addForm, mother_name: e.target.value })}
                    placeholder="Mother's Name"
                    className="w-full border border-slate-300 rounded p-2 text-xs focus:ring-1 focus:ring-[#0F2C59]"
                  />
                </div>

                {/* Gender */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Gender</label>
                  <select
                    value={addForm.gender}
                    onChange={(e) => setAddForm({ ...addForm, gender: e.target.value })}
                    className="w-full border border-slate-300 rounded p-2 text-xs font-semibold focus:ring-1 focus:ring-[#0F2C59]"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Class */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Class</label>
                  <select
                    value={addForm.class_id}
                    onChange={(e) => setAddForm({ ...addForm, class_id: e.target.value })}
                    className="w-full border border-slate-300 rounded p-2 text-xs font-bold text-[#0F2C59] focus:ring-1 focus:ring-[#0F2C59]"
                  >
                    <option value="1">Class 10 (Secondary)</option>
                    <option value="2">Class 12 (Senior Secondary)</option>
                  </select>
                </div>

                {/* Stream (Only enabled for Class 12) */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">
                    Stream {addForm.class_id === '1' && <span className="text-slate-400 font-normal">(N/A for Class 10)</span>}
                  </label>
                  <select
                    disabled={addForm.class_id === '1'}
                    value={addForm.stream}
                    onChange={(e) => setAddForm({ ...addForm, stream: e.target.value })}
                    className="w-full border border-slate-300 rounded p-2 text-xs font-semibold disabled:bg-slate-100 disabled:text-slate-400 focus:ring-1 focus:ring-[#0F2C59]"
                  >
                    <option value="Arts">Arts</option>
                    <option value="Science">Science</option>
                    <option value="Commerce">Commerce</option>
                  </select>
                </div>

                {/* Section */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Section</label>
                  <input
                    type="text"
                    value={addForm.section}
                    onChange={(e) => setAddForm({ ...addForm, section: e.target.value.toUpperCase() })}
                    placeholder="A"
                    maxLength={3}
                    className="w-full border border-slate-300 rounded p-2 text-xs font-bold text-center focus:ring-1 focus:ring-[#0F2C59]"
                  />
                </div>

                {/* Academic Year */}
                <div className="col-span-2">
                  <label className="block font-bold text-slate-700 mb-1">Academic Session / Year</label>
                  <input
                    type="text"
                    disabled
                    value="2026-27 (Active Academic Session)"
                    className="w-full border border-slate-200 bg-slate-100 rounded p-2 text-xs font-semibold text-slate-600"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setAddModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded font-semibold text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addSaving}
                  className="px-4 py-2 bg-[#0F2C59] hover:bg-[#1E40AF] text-white rounded font-bold text-xs flex items-center space-x-1.5 disabled:opacity-50"
                >
                  <UserPlus className="w-3.5 h-3.5 text-amber-400" />
                  <span>{addSaving ? 'Registering...' : 'Save & Register Student'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. EDIT STUDENT MODAL */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full overflow-hidden border border-slate-300 animate-in fade-in zoom-in duration-150">
            <div className="p-4 bg-[#0F2C59] text-white flex justify-between items-center">
              <div>
                <div className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">
                  Update Student Registry Record
                </div>
                <h3 className="text-base font-bold">Edit Student Details</h3>
              </div>
              <button
                onClick={() => setEditModalOpen(false)}
                className="p-1 rounded hover:bg-white/20 text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-5 space-y-4 text-xs">
              {editError && (
                <div className="p-3 bg-rose-50 border border-rose-300 rounded text-rose-900 font-bold flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  <span>{editError}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {/* SRN (Read only) */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">SRN (Immutable)</label>
                  <input
                    type="text"
                    disabled
                    value={editForm.srn}
                    className="w-full border border-slate-200 bg-slate-100 rounded p-2 text-xs font-mono font-bold text-slate-600"
                  />
                </div>

                {/* Student Name */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">
                    Student Name <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={editForm.student_name}
                    onChange={(e) => setEditForm({ ...editForm, student_name: e.target.value })}
                    className="w-full border border-slate-300 rounded p-2 text-xs font-bold focus:ring-1 focus:ring-[#0F2C59]"
                  />
                </div>

                {/* Father Name */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Father's Name</label>
                  <input
                    type="text"
                    value={editForm.father_name}
                    onChange={(e) => setEditForm({ ...editForm, father_name: e.target.value })}
                    className="w-full border border-slate-300 rounded p-2 text-xs focus:ring-1 focus:ring-[#0F2C59]"
                  />
                </div>

                {/* Mother Name */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Mother's Name</label>
                  <input
                    type="text"
                    value={editForm.mother_name}
                    onChange={(e) => setEditForm({ ...editForm, mother_name: e.target.value })}
                    className="w-full border border-slate-300 rounded p-2 text-xs focus:ring-1 focus:ring-[#0F2C59]"
                  />
                </div>

                {/* Gender */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Gender</label>
                  <select
                    value={editForm.gender}
                    onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                    className="w-full border border-slate-300 rounded p-2 text-xs font-semibold focus:ring-1 focus:ring-[#0F2C59]"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Class */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Class</label>
                  <select
                    value={editForm.class_id}
                    onChange={(e) => setEditForm({ ...editForm, class_id: e.target.value })}
                    className="w-full border border-slate-300 rounded p-2 text-xs font-bold text-[#0F2C59] focus:ring-1 focus:ring-[#0F2C59]"
                  >
                    <option value="1">Class 10</option>
                    <option value="2">Class 12</option>
                  </select>
                </div>

                {/* Stream */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">
                    Stream {editForm.class_id === '1' && <span className="text-slate-400 font-normal">(N/A for Class 10)</span>}
                  </label>
                  <select
                    disabled={editForm.class_id === '1'}
                    value={editForm.stream}
                    onChange={(e) => setEditForm({ ...editForm, stream: e.target.value })}
                    className="w-full border border-slate-300 rounded p-2 text-xs font-semibold disabled:bg-slate-100 disabled:text-slate-400 focus:ring-1 focus:ring-[#0F2C59]"
                  >
                    <option value="Arts">Arts</option>
                    <option value="Science">Science</option>
                    <option value="Commerce">Commerce</option>
                  </select>
                </div>

                {/* Section */}
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Section</label>
                  <input
                    type="text"
                    value={editForm.section}
                    onChange={(e) => setEditForm({ ...editForm, section: e.target.value.toUpperCase() })}
                    placeholder="A"
                    maxLength={3}
                    className="w-full border border-slate-300 rounded p-2 text-xs font-bold text-center focus:ring-1 focus:ring-[#0F2C59]"
                  />
                </div>

                {/* Student Status */}
                <div className="col-span-2">
                  <label className="block font-bold text-slate-700 mb-1">Enrollment / Attendance Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full border border-slate-300 rounded p-2 text-xs font-bold text-slate-900 bg-slate-50 focus:ring-1 focus:ring-[#0F2C59]"
                  >
                    <option value="ACTIVE">ACTIVE (Currently Attending)</option>
                    <option value="TRANSFERRED">TRANSFERRED (Transferred to Another School)</option>
                    <option value="LEFT SCHOOL">LEFT SCHOOL (Left / Struck Off)</option>
                    <option value="DROPPED OUT">DROPPED OUT (Discontinued Studies)</option>
                    <option value="PASSED/COMPLETED">PASSED/COMPLETED (Completed Board Exams)</option>
                    <option value="INACTIVE">INACTIVE</option>
                    <option value="OTHER">OTHER</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setEditModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded font-semibold text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSaving}
                  className="px-4 py-2 bg-[#0F2C59] hover:bg-[#1E40AF] text-white rounded font-bold text-xs flex items-center space-x-1.5 disabled:opacity-50"
                >
                  <Edit2 className="w-3.5 h-3.5 text-amber-400" />
                  <span>{editSaving ? 'Saving Changes...' : 'Update Student Details'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. ASSESSMENT HISTORY MODAL */}
      {historyModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full overflow-hidden border border-slate-300 animate-in fade-in zoom-in duration-150">
            <div className="p-4 bg-[#0F2C59] text-white flex justify-between items-center">
              <div>
                <div className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">
                  Monthly Assessment History
                </div>
                <h3 className="text-base font-bold">
                  {historyData?.student?.student_name || 'Student Assessment Record'}
                </h3>
                <div className="text-xs text-slate-300">
                  SRN: {historyData?.student?.srn} • {historyData?.student?.class_name} - Section {historyData?.student?.section || 'A'}
                </div>
              </div>
              <button
                onClick={() => setHistoryModalOpen(false)}
                className="p-1 rounded hover:bg-white/20 text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 max-h-[70vh] overflow-y-auto space-y-4 text-xs">
              {historyLoading ? (
                <div className="py-8 text-center text-slate-500">
                  <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-[#0F2C59] border-t-transparent mb-2"></div>
                  <div>Loading chronological assessment history...</div>
                </div>
              ) : !historyData || historyData.history.length === 0 ? (
                <div className="py-8 text-center text-slate-500">
                  No monthly assessment records found for this student.
                </div>
              ) : (
                <div className="border border-slate-200 rounded overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[11px] border-b border-slate-200">
                      <tr>
                        <th className="py-2.5 px-3">Month</th>
                        <th className="py-2.5 px-3">Class</th>
                        <th className="py-2.5 px-3.5">Subject</th>
                        <th className="py-2.5 px-3 text-center">Status</th>
                        <th className="py-2.5 px-3 text-right">Marks</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {historyData.history.map((h, i) => {
                        const isAbsent = h.is_absent === 1 || h.remarks === 'ABSENT' || h.mark_status === 'absent';
                        const isNotApplicable = h.remarks === 'NOT_APPLICABLE' || h.mark_status === 'not_applicable';
                        
                        let statusText = 'PRESENT';
                        let badgeClass = 'bg-emerald-100 text-emerald-800 border-emerald-300';
                        if (isAbsent) {
                          statusText = 'ABSENT';
                          badgeClass = 'bg-rose-100 text-rose-800 border-rose-300';
                        } else if (isNotApplicable) {
                          statusText = 'NOT APPLICABLE';
                          badgeClass = 'bg-slate-100 text-slate-700 border-slate-300';
                        }

                        return (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="py-2 px-3 font-bold text-slate-900">{h.month}</td>
                            <td className="py-2 px-3 text-slate-700 font-medium">{h.class_name}</td>
                            <td className="py-2 px-3.5 font-semibold text-[#0F2C59]">{h.subject_name}</td>
                            <td className="py-2 px-3 text-center">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${badgeClass}`}>
                                {statusText}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-right font-bold text-slate-900">
                              {isAbsent ? (
                                <span className="text-rose-600">ABSENT</span>
                              ) : isNotApplicable ? (
                                <span className="text-slate-400">-</span>
                              ) : (
                                <span>
                                  {h.obtained_marks !== null ? `${h.obtained_marks} / ${h.maximum_marks}` : '-'}
                                  {h.percentage ? <span className="ml-1 text-[11px] text-emerald-700">({h.percentage}%)</span> : null}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="p-3 bg-slate-50 border-t border-slate-200 text-right">
              <button
                onClick={() => setHistoryModalOpen(false)}
                className="px-4 py-1.5 bg-[#0F2C59] text-white font-semibold rounded text-xs"
              >
                Close History
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 4. STUDENT 360° PROFILE MODAL */}
      {profileModalOpen && selectedStudent && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-xl w-full overflow-hidden border border-slate-300 animate-in fade-in zoom-in duration-150">
            <div className="p-4 bg-[#0F2C59] text-white flex justify-between items-center">
              <div>
                <div className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">
                  Student 360° Academic Record
                </div>
                <h3 className="text-base font-bold">{selectedStudent.student.student_name}</h3>
                <div className="text-xs text-slate-300">
                  SRN: {selectedStudent.student.srn} • {selectedStudent.student.school_name}
                </div>
              </div>
              <button
                onClick={() => setProfileModalOpen(false)}
                className="p-1 rounded hover:bg-white/20 text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 max-h-[75vh] overflow-y-auto space-y-4 text-xs">
              <div className="grid grid-cols-3 gap-2 bg-slate-50 p-3 rounded border border-slate-200">
                <div>
                  <div className="text-slate-500 text-[10px]">Class & Section</div>
                  <div className="font-bold text-slate-900">{selectedStudent.student.class_name} - {selectedStudent.student.section || 'A'}</div>
                </div>
                <div>
                  <div className="text-slate-500 text-[10px]">Gender</div>
                  <div className="font-bold text-slate-900">{selectedStudent.student.gender || 'Female'}</div>
                </div>
                <div>
                  <div className="text-slate-500 text-[10px]">Block</div>
                  <div className="font-bold text-slate-900">{selectedStudent.student.block}</div>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] mb-2 flex items-center space-x-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-blue-600" />
                  <span>Monthly Assessment Scorecard (2026-27)</span>
                </h4>
                <div className="border border-slate-200 rounded overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-100 text-slate-700 font-bold">
                      <tr>
                        <th className="py-2 px-3">Month</th>
                        <th className="py-2 px-3">Subject</th>
                        <th className="py-2 px-3 text-right">Marks</th>
                        <th className="py-2 px-3 text-right">Percentage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {selectedStudent.marks_history && selectedStudent.marks_history.length > 0 ? (
                        selectedStudent.marks_history.map((m: any, i: number) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="py-1.5 px-3 font-semibold text-slate-900">{m.month}</td>
                            <td className="py-1.5 px-3 text-slate-700">{m.subject}</td>
                            <td className="py-1.5 px-3 text-right font-bold text-slate-900">
                              {m.is_absent ? <span className="text-rose-600">ABSENT</span> : `${m.marks}/${m.max}`}
                            </td>
                            <td className="py-1.5 px-3 text-right font-extrabold text-emerald-700">
                              {m.is_absent ? '-' : `${m.percentage}%`}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="py-4 text-center text-slate-500">No monthly marks recorded.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="p-3 bg-slate-50 border-t border-slate-200 text-right">
              <button
                onClick={() => setProfileModalOpen(false)}
                className="px-4 py-1.5 bg-[#0F2C59] text-white font-semibold rounded text-xs"
              >
                Close Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
